/** Nombres de rol tratados como visualizador (filtro de errores). */
const VIEWER_ROLE_NAMES = ['visualizador cliente', 'cliente'];

const namesMatch = (left, right) =>
  String(left || '').toLowerCase() === String(right || '').toLowerCase();

const hasFunction = (privileges, key, expectedName) => {
  const functions = privileges?.[key]?.function;
  if (!Array.isArray(functions)) {
    return false;
  }
  return functions.some((func) => namesMatch(func.name, expectedName));
};

/**
 * Acepta las dos formas del JSON:
 * - privileges.enrichment.function contiene CREATE (Flask / validate_privilege)
 * - privileges.CREATE.function contiene enrichment (asignación invertida en BD)
 */
const hasPair = (privileges, moduleName, functionName) =>
  hasFunction(privileges, moduleName, functionName) ||
  hasFunction(privileges, functionName, moduleName);

export const getPrivileges = () => {
  try {
    return JSON.parse(localStorage.getItem('privileges') || '{}');
  } catch {
    return {};
  }
};

/** Equivale a validate_privilege del backend: modulo + función (CREATE, READ, DOWNLOAD, DELETE). */
export const hasPrivilege = (moduleName, functionName) =>
  hasPair(getPrivileges(), moduleName, functionName);

/** Clave donde se persiste el rol GLOBAL del usuario (fuente de verdad). */
const ACTIVE_ROLE_KEY = 'activeRole';

export const persistRolePrivileges = (role) => {
  if (!role) {
    return;
  }

  const privileges = role.privileges || {};
  if (role.nombre != null) {
    localStorage.setItem('name', role.nombre);
    localStorage.setItem('roleName', role.nombre);
  }
  localStorage.setItem('privileges', JSON.stringify(privileges));
  localStorage.setItem('privilege', String(hasPair(privileges, 'admin', 'UPDATE')));
  localStorage.setItem('ENRICHMENT', String(hasPair(privileges, 'enrichment', 'CREATE')));
  localStorage.setItem('CLONES', String(hasPair(privileges, 'clones', 'CREATE')));
  localStorage.setItem('CLONES_MANUALES', String(hasPair(privileges, 'clones_manuales', 'CREATE')));
};

/**
 * Persiste el rol GLOBAL del usuario (active_role del login) como fuente de
 * verdad y aplica sus permisos. Llamar una sola vez en el login.
 */
export const persistActiveRole = (role) => {
  if (role) {
    localStorage.setItem(ACTIVE_ROLE_KEY, JSON.stringify(role));
  }
  persistRolePrivileges(role);
};

/**
 * Re-aplica los permisos del rol global persistido. Se usa al cambiar de
 * organización: los permisos dependen del rol del usuario (login), NO de la
 * organización, así que siempre se restauran desde active_role.
 * Devuelve true si se pudo re-aplicar.
 */
export const reapplyActiveRolePrivileges = () => {
  const stored = localStorage.getItem(ACTIVE_ROLE_KEY);
  if (!stored || stored === 'undefined') {
    return false;
  }
  try {
    persistRolePrivileges(JSON.parse(stored));
    return true;
  } catch {
    return false;
  }
};

export const clearRolePrivileges = () => {
  localStorage.removeItem('privilege');
  localStorage.removeItem('privileges');
  localStorage.removeItem('roleName');
  localStorage.removeItem('ENRICHMENT');
  localStorage.removeItem('CLONES');
  localStorage.removeItem('CLONES_MANUALES');
  localStorage.removeItem(ACTIVE_ROLE_KEY);
};

const hasStoredPrivileges = () => localStorage.getItem('privileges') != null;

export const canCreateEnrichment = () => hasPrivilege('enrichment', 'CREATE');

export const canDownloadEnrichment = () => hasPrivilege('enrichment', 'DOWNLOAD');

export const canDeleteEnrichment = () => hasPrivilege('enrichment', 'DELETE');

export const canDownloadClones = () =>
  hasPrivilege('clones', 'DOWNLOAD') || hasPrivilege('clones_manuales', 'DOWNLOAD');

export const canDeleteClones = () =>
  hasPrivilege('clones', 'DELETE') || hasPrivilege('clones_manuales', 'DELETE');

export const canAccessAmplify = () =>
  hasStoredPrivileges()
    ? hasPrivilege('clones', 'CREATE') ||
      hasPrivilege('clones', 'READ') ||
      hasPrivilege('clones_manuales', 'CREATE')
    : localStorage.getItem('CLONES') !== 'false' ||
      localStorage.getItem('CLONES_MANUALES') !== 'false';

export const isViewerRole = () => {
  const name = (localStorage.getItem('roleName') || localStorage.getItem('name') || '').toLowerCase().trim();
  return VIEWER_ROLE_NAMES.includes(name);
};

/** Visualizador: sin DELETE/CREATE, o nombre Cliente / Visualizador Cliente. */
export const shouldHideErrorRecords = () =>
  isViewerRole() || (hasStoredPrivileges() && !canDeleteEnrichment() && !canCreateEnrichment());

export const isBuyerErrorStatus = (action_) => {
  const action = Number(action_);
  if (Number.isNaN(action)) {
    return false;
  }
  return action === 0 || action === 99 || action === -5 || (action < -899 && action > -951);
};
